import { FortuneReleaseBadge } from '@/app_components/release-tools/FortuneReleaseBadge';
import { buildSignInRedirectUrl } from '@/app_lib/utils/signinRedirect';
import Button from 'components/Globals/Base/Button';
import Link from 'components/Globals/Base/Link';
import { DrawerToggleButton } from 'components/Header/HeaderStyles';
import { SignInWrapper } from 'components/Header/Menu/MenuStyles';
import { Icon } from 'components/Icons/index';
import { showInstreamAd } from 'components/NavigationBar';
import BackToFortune from 'components/recommends/BackToFortune';
import { Microsites, sourceSubscribeCTA } from 'constants/constants';
import DRAWER_MENU_TEST_IDS from 'constants/testsIds/drawerMenu';
import useBreakpoint from 'hooks/useBreakpoint';
import { Logo } from 'icons/Logo';
import type CustomWindow from 'interfaces/Utils';
import { UserContext } from 'providers/UserProvider';
import { type ForwardedRef, forwardRef, type JSX, useContext } from 'react';
import { trackEvent } from 'services/Gtm/functions';
import * as S from './DrawerMenuStyles';

declare let window: CustomWindow;

interface DrawerMenuProps {
  children: JSX.Element | JSX.Element[];
  subDomain?: string;
  handleClick: () => void;
  onSearchClick: () => void;
  keyboardToggle: () => void;
  showStickyFooter?: boolean;
}

const DrawerMenu = (
  { children, handleClick, keyboardToggle, onSearchClick, subDomain, showStickyFooter }: DrawerMenuProps,
  ref: ForwardedRef<HTMLDivElement> | null,
) => {
  // const isTablet = useBreakpoint('mdMin');
  const isMobile = useBreakpoint('mdMax');
  const { userState: user, pianoScriptFailed, showPaywallax } = useContext(UserContext);

  const isRecommendsOrEdu =
    !process.env.FEATURE_FLAG_DISABLE_RECOMMENDS &&
    [Microsites.Recommends, Microsites.Education].includes(String(subDomain));

  const isSubscribeBtnDisplayed = !user.hasAccess && !user.isPaused;

  const subscribeBtnTrackingData = {
    contentPlacementCD: 'header',
    eventAction: 'top header links',
    eventCategory: 'navigation',
    eventLabel: 'subscribe now',
    loggedIn: user?.isLoggedIn,
  };

  const closeIconTrackingData = {
    contentPlacementCD: 'Global Side navigation',
    eventAction: 'Global Side navigation toggle',
    eventCategory: 'navigation',
    eventLabel: 'close',
  };
  const trackingData = {
    contentPlacementCD: 'header',
    eventAction: 'top header links',
    eventCategory: 'navigation',
  };
  const redirect = typeof window !== 'undefined' ? window.location.pathname + window.location.search : '';

  const signInRedirect =
    typeof window !== 'undefined' ? buildSignInRedirectUrl(window.location, 'relative') : '/signin';

  const handleKeyDown = (event: React.KeyboardEvent<HTMLLabelElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleClick();
      keyboardToggle();
    }
  };

  return (
    <>
      <S.ContainerDrawer
        className='drawer-menu'
        ref={ref}
        data-cy={DRAWER_MENU_TEST_IDS.CONTAINER}
        $isLogIn={user.isLoggedIn || false}
        $isSubscribeBtn={isSubscribeBtnDisplayed}
      >
        <S.CloseWrapper>
          <S.TopWrapper $isSubscribeBtn={isSubscribeBtnDisplayed}>
            <S.TopLeftWrapper>
              <DrawerToggleButton
                htmlFor='drawer-toggle'
                id='drawer-toggle-button'
                className='close-menu'
                onClick={() => {
                  if (window.s2nApi) {
                    // The sticky footer is only enabled for mobile and non-paywalled articles
                    // STN Video should then only NOT float when both conditions are met
                    if (!showPaywallax && showStickyFooter) {
                      window.s2nApi.float = false;
                    } else {
                      window.s2nApi.float = true;
                    }
                  }
                  if (trackEvent && closeIconTrackingData) trackEvent(closeIconTrackingData);
                }}
                onKeyDown={handleKeyDown}
                aria-label='Close menu'
                data-cy='closeButton'
                tabIndex={0}
              >
                <Icon
                  icon='X'
                  size='24'
                  className='close-icon'
                />
              </DrawerToggleButton>
              <Link
                href='/'
                ariaLabel='Homepage'
                className='mobile-logo'
                dataCy='logo'
              >
                <Logo />
                <FortuneReleaseBadge />
              </Link>
            </S.TopLeftWrapper>
            <S.TopRightWrapper>
              <Button
                type='button'
                onClick={() => {
                  onSearchClick();
                  showInstreamAd(subDomain);
                }}
                data-cy='searchButton'
                variant='tertiaryBlack'
                className='search-button'
                hasRightArrow={false}
              >
                <label htmlFor='queryly_toggle'>
                  <Icon
                    icon='Search'
                    size='24'
                  />
                </label>
              </Button>
            </S.TopRightWrapper>
          </S.TopWrapper>
        </S.CloseWrapper>
        {children}
        {(!user.isLoggedIn || isSubscribeBtnDisplayed) && pianoScriptFailed === false && (
          <S.BottomWrapper className={`${isRecommendsOrEdu ? 'education' : ''}`}>
            {isMobile && !user.isLoggedIn && (
              <SignInWrapper className={subDomain}>
                <Link
                  className='sign-in-button'
                  href={signInRedirect}
                  dataCy='signinBtnMobile'
                  trackingData={{
                    ...trackingData,
                    eventLabel: 'sign in',
                  }}
                  type='whiteButton'
                  hasRightArrow={false}
                >
                  Sign In
                </Link>
              </SignInWrapper>
            )}
            {isMobile && isSubscribeBtnDisplayed && (
              <S.MyAccountWrapper>
                <Link
                  className='subscribe-link'
                  href={`/subscribe/?itm_source=${sourceSubscribeCTA}&redirect=${encodeURIComponent(redirect)}`}
                  trackingData={subscribeBtnTrackingData}
                  dataCy='subscribeBtnDrawer'
                  type='accentButton'
                  hasRightArrow={false}
                >
                  Subscribe for $1
                </Link>
              </S.MyAccountWrapper>
            )}
          </S.BottomWrapper>
        )}
        {isRecommendsOrEdu && <BackToFortune isInDrawerMenu />}
      </S.ContainerDrawer>
      <S.OutsideTheMenu
        className='overlay'
        onClick={handleClick}
      />
    </>
  );
};

export default forwardRef(DrawerMenu);
